import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { Button, FormControl, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { IListItem, IWorkflowStatus } from '@nkr/models/index';
import { WorkflowStatusActionType } from '@nkr/enums/index';

interface IProps {
    list: IWorkflowStatus[],
    dispositionList: IListItem[],
    sortOrderList: any[],
    onGridReady: (e: GridReadyEvent) => void,
    onUpdateConfirmation: (actionType, statusId, value) => void,
    onSortOrderChange: (statusId: number, sortOrderId: number) => void
}

const Grid = (props: IProps) => {
    const columns: ColDef[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerName: 'Rank',
            width: 60,
            sortable: false,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },

        {
            field: 'description',
            headerName: 'Status Name',
            width: 250
        },

        {
            colId: 'dispositionId',
            field: 'dispositionId',
            headerName: 'Applicable Disposition',
            width: 225,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IWorkflowStatus;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            value={data.associatedDisposition}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onUpdateConfirmation(WorkflowStatusActionType.DispositionUpdate, data.id, parseInt(e.currentTarget.value))}
                        >
                            {
                                props.dispositionList.map((item, index) =>
                                    <option key={'disposition-' + index} value={item.id}>{item.description}</option>
                                )
                            }
                        </FormControl>
                    </>
                )
            }
        },

        {
            colId: 'followUp',
            field: 'addFollowUp',
            headerName: 'Needs Follow Up',
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IWorkflowStatus;
                return (
                    <>
                        <ToggleButtonGroup type='radio' name='swaps' value={data.addFollowUp}>
                            <ToggleButton type='radio' value={1} className={data.addFollowUp == true ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.followUpUpdate, data.id, true)}>Yes</ToggleButton>
                            <ToggleButton type='radio' value={2} className={data.addFollowUp == false ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.followUpUpdate, data.id, false)}>No</ToggleButton>
                        </ToggleButtonGroup>
                    </>
                )
            }
        },

        {
            colId: 'isInEmr',
            field: 'isInEmr',
            headerName: 'Is in EMR',
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IWorkflowStatus;
                return (
                    <>
                        <ToggleButtonGroup type='radio' name='swaps' value={data.isInEmr}>
                            <ToggleButton type='radio' value={1} className={data.isInEmr == true ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.isInEmrUpdate, data.id, true)}>Yes</ToggleButton>
                            <ToggleButton type='radio' value={2} className={data.isInEmr == false ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.isInEmrUpdate, data.id, false)}>No</ToggleButton>
                        </ToggleButtonGroup>
                    </>
                )
            }
        },

        {
            colId: 'isRuledOut',
            field: 'isRuledOut',
            headerName: 'Is Ruled Out',
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IWorkflowStatus;
                return (
                    <>
                        <ToggleButtonGroup type='radio' name='swaps' value={data.isRuledOut}>
                            <ToggleButton type='radio' value={1} className={data.isRuledOut == true ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.isRuledOutUpdate, data.id, true)}>Yes</ToggleButton>
                            <ToggleButton type='radio' value={2} className={data.isRuledOut == false ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.isRuledOutUpdate, data.id, false)}>No</ToggleButton>
                        </ToggleButtonGroup>
                    </>
                )
            }
        },

        {
            colId: 'sortOrder',
            field: 'sortOrder',
            headerName: 'Sort Order',
            width: 100,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IWorkflowStatus;

                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            style={{ width: '65px' }}
                            value={data.sortOrder}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onSortOrderChange(data.id, parseInt(e.currentTarget.value))}
                        >                                                        {
                                props.sortOrderList.map((name, index) =>
                                    <option key={'sortOrder-' + index} value={index}>{name}</option>
                                )
                            }
                        </FormControl>
                    </>
                )
            }
        },

        {
            colId: 'delete',
            field: 'delete',
            headerName: 'Delete',
            width: 90,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IWorkflowStatus;

                return (
                    <>
                        <Button bsSize='xsmall' onClick={() => props.onUpdateConfirmation(WorkflowStatusActionType.Delete, data.id, 0)}
                            bsStyle='danger glyphicon glyphicon-remove'>
                        </Button>
                    </>
                )
            }
        },
    ];

    const columnDefs = useMemo(() => columns, [props.dispositionList]);
    const rowData = useMemo(() => props.list, [props.list]);

    return (
        <GridWrapper id='grid-wrapper' className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    sortable: true,
                    suppressMenu: true,
                }}
                rowData={rowData}
                headerHeight={32}
                rowHeight={35}
                noRowsOverlayComponentFramework={() => <div>No workflow status' found...</div>}
                suppressCellSelection={true}
                suppressContextMenu={true}
                onGridReady={props.onGridReady}
                onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.sizeColumnsToFit()}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    );
}

export default Grid;